import { useState, useEffect, useRef } from "react";
import { documents as docsApi, getAccount } from "../lib/supabase.js";

// QR Code generation using the QR Server API (no dependency needed)
function qrUrl(text, size = 200) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=ffffff&color=000000&margin=8`;
}

export default function QRGenerator() {
  const account = getAccount();
  const color = account?.widget_color || "#6366f1";
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const floorBaseUrl = `${window.location.origin}/floor?account=${account?.slug || ""}`;

  useEffect(() => {
    docsApi.list().then(({ data }) => {
      setDocs((data || []).filter((d) => d.status === "indexed"));
      setLoading(false);
    });
  }, []);

  const toggleSelect = (id) => {
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const selectAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map((d) => d.id)));
  };

  const selectedDocs = docs.filter((d) => selected.has(d.id));

  const printQRCodes = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const html = `<!DOCTYPE html>
<html><head><title>QR Codes — ${account?.name || ""}</title>
<style>
  body { font-family: -apple-system, sans-serif; margin: 0; padding: 20px; }
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .card { border: 1px solid #ddd; border-radius: 12px; padding: 16px; text-align: center; page-break-inside: avoid; }
  .card img { display: block; margin: 0 auto 10px; }
  .card h3 { font-size: 13px; margin: 0 0 4px; color: #333; word-break: break-word; }
  .card p { font-size: 10px; color: #888; margin: 0; }
  @media print { body { padding: 10px; } .grid { gap: 12px; } .no-print { display: none; } }
</style></head><body>
<div class="no-print" style="margin-bottom: 20px;">
  <h1 style="font-size: 18px; margin: 0 0 4px;">${account?.name || ""} — Document QR Codes</h1>
  <p style="font-size: 13px; color: #888; margin: 0 0 12px;">${selectedDocs.length} document${selectedDocs.length !== 1 ? "s" : ""}</p>
  <button onclick="window.print()" style="padding: 8px 20px; background: ${color}; border: none; border-radius: 8px; color: #fff; font-size: 14px; cursor: pointer;">Print</button>
</div>
<div class="grid">
  ${selectedDocs.map((d) => {
    const url = `${floorBaseUrl}&doc=${d.id}`;
    return `<div class="card">
      <img src="${qrUrl(url, 180)}" width="180" height="180" alt="QR" />
      <h3>${d.original_filename || d.filename}</h3>
      <p>${d.category || ""} ${d.page_count ? `· ${d.page_count} pages` : ""}</p>
    </div>`;
  }).join("\n")}
</div></body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e0e0e0" }}>QR Code Labels</div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>Generate printable QR codes that link directly to floor access for each document</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={selectAll} style={{ padding: "7px 14px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 6, color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
            {selected.size === docs.length ? "Deselect All" : "Select All"}
          </button>
          {selected.size > 0 && (
            <button onClick={printQRCodes} style={{ padding: "7px 14px", background: color, border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              🖨 Print {selected.size} QR Code{selected.size !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#444", fontSize: 13, padding: 20 }}>Loading documents...</div>
      ) : docs.length === 0 ? (
        <div style={{ color: "#444", fontSize: 13, padding: 20, textAlign: "center" }}>No indexed documents available for QR codes.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {docs.map((d) => {
            const url = `${floorBaseUrl}&doc=${d.id}`;
            const isSelected = selected.has(d.id);
            return (
              <div
                key={d.id}
                onClick={() => toggleSelect(d.id)}
                style={{
                  background: isSelected ? `${color}08` : "#111118",
                  border: `1px solid ${isSelected ? `${color}40` : "#1e1e2e"}`,
                  borderRadius: 10, padding: 14, textAlign: "center",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <img src={qrUrl(url, 140)} width={140} height={140} alt="QR" style={{ display: "block", margin: "0 auto 10px", borderRadius: 6 }} />
                <div style={{ fontSize: 12, color: "#ccc", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.original_filename || d.filename}
                </div>
                <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                  {d.category || "uncategorized"}
                </div>
                <div style={{ marginTop: 8 }}>
                  <span style={{
                    padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                    background: isSelected ? `${color}20` : "#1a1a2a",
                    color: isSelected ? color : "#666",
                  }}>
                    {isSelected ? "✓ Selected" : "Select"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
